import { h } from 'preact';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation } from 'swiper';
import { useEffect, useRef, useState } from 'preact/hooks';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import ProductCard from 'utils/product-card';
import { edgesToNodes } from 'utils/storefront/format-resource';
import ChevronRightIcon from 'utils/icon/icon-chevron-right.svg';
import clsx from 'clsx';
import useUniqueId from 'utils/unique-id';
import { decodeId } from 'utils/helpers';

const ProductRecommendations = (props) => {
  const storefront = Storefront.getInstance();
  const [products, setProducts] = useState([]);
  const id = useUniqueId();
  const recommendationsRef = useRef(null);
  const debounce = (func, delay) => {
    let inDebounce;
    return (...args) => {
      clearTimeout(inDebounce);
      inDebounce = setTimeout(() => func.apply(this, args), delay);
    };
  };
  const getProducts = async (data) => {
    const ids = data.map(
      (product) => `gid://shopify/Product/${product.product_id}`,
    );

    const response = await storefront.request({
      query: queries.getProductsByIds,
      variables: {
        ids,
      },
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error(
        'product-recommendations/product-recommendations.js: Storefront API request failed',
        response,
      );
      return [];
    }

    const existingProducts = response.data.nodes.filter(
      (product) => product != null && product.availableForSale,
    );

    if (!existingProducts.length) {
      return [];
    }

    return edgesToNodes(existingProducts, 'product');
  };

  const onRecommendationsLoaded = (data) => {
    if (data && data.length > 0) {
      // track event recommendation action=show
      // often, the recommended items are placed below the fold, for proper evalution track "show" event after customer scrolls and see recommended items
      const itemIds = data.map((product) => product.item_id);
      // eslint-disable-next-line no-undef
      exponea.track('recommendation', {
        action: 'show',
        item_ids: itemIds,
        recommendation_id: props.recommendation_id,
        placement: props.placement,
      });
      getProducts(data).then((items) => {
        setProducts(items);
      });
    }
  };

  const getProductsData = (productId, variantId) => {
    const options = {
      recommendationId: props.recommendation_id,
      size: props.size, // Specifies upper limit of the recommendations to return. Defaults to 10.
      callback: onRecommendationsLoaded,
      fillWithRandom: props.fill_with_random, // If true, fills the recommendations with random items until size is reached. This is utilized when models cannot recommend enough items.
    };
    switch (props?.bloomreach_id_type) {
      case 'product_id':
        if (productId) {
          options.items = { [productId]: 1 };
        }
        break;
      case 'variant_id':
        if (variantId) {
          options.items = { [variantId]: 1 };
        }
        break;
      default:
        break;
    }
    // call recommedations
    // eslint-disable-next-line no-undef
    exponea.getRecommendation(options);
  };

  useEffect(() => {
    if (props?.type === 'manual') {
      getProducts(props.products).then((items) => setProducts(items));
    } else {
      getProductsData(props.current_product_id, props?.current_variant_id);
    }
    const productUpdateHandle = (e) => {
      if (props?.type !== 'manual') {
        getProductsData(
          decodeId(e.detail.id, 'Product'),
          decodeId(e.detail.variants[0].id, 'ProductVariant'),
        );
      } else if (props?.metafield !== 'none') {
        const fields = {
          'details.pairs_with': 'pairsWith',
          'details.shop_the_look': 'shopTheLook',
          'details.model_also_wearing': 'modelWearing',
          'look.look_products': 'shopTheLook',
        };
        const metafieldValue = props?.metafield === 'details.pairs_with'
        ? e.detail[fields[props?.metafield]]
        : e.detail[fields[props?.metafield]]?.value;
        if (metafieldValue) {
          getProducts(
            // eslint-disable-next-line no-shadow
            JSON.parse(metafieldValue).map((id) => ({
              product_id: decodeId(id, 'Product'),
            })),
          ).then((items) => {
            setProducts(items);
          });
        } else if (props.products?.length && !props.metafield_products) {
          getProducts(props.products).then((items) => {
            setProducts(items);
          });
        } else {
          setProducts([]);
        }
      }
    };
    document.addEventListener('productUpdated', productUpdateHandle);
    return () => {
      document.removeEventListener('productUpdated', productUpdateHandle);
    };
  }, []);

  useEffect(() => {
    if (!products.length) return;
    document.dispatchEvent(
      new CustomEvent('recommendations:init', {
        detail: {
          sectionId: `#section--${props.section_id}`,
        },
      }),
    );
  }, [products]);

  const handleSwiperInit = () => {
    if (recommendationsRef.current) {
      recommendationsRef.current.classList.add('swiper-no-transform');
      setTimeout(() => {
        recommendationsRef.current.classList.remove('swiper-no-transform');
      }, 0);
    }
  };

  useEffect(() => {
    const debouncedHandleResize = debounce(handleSwiperInit, 250);
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [handleSwiperInit]);

  return (
    products.length > 0 && (
      <div
        id="product-recommendations"
        className="mb-40 product-recommendations"
        data-slider={id}
        ref={recommendationsRef}
      >
        <div className="flex justify-between bg-grey-0">
          <div className="relative w-full">
            <div className="container relative">
              <div className="col-span-full mt-40 mb-24 ml:mb-32 flex justify-between">
                <div className="relative w-full ml:md:px-75 flex justify-between ml:justify-center">
                  <h1
                    className="display-4 normal-case ml:text-center line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: props.header }}
                  ></h1>
                  <div
                    className={clsx(
                      'product-recommendations-arrows hidden ml:absolute ml:-top-4 ml:right-0 lc:pt-2',
                      {
                        'md:flex': products.length > 4,
                      },
                    )}
                  >
                    <button className="prev w-32 h-32 rounded-full lc:border border-grey-900 flex justify-center items-center mx-4 lc:mx-8 pr-2">
                      <ChevronRightIcon className="w-16 ml:w-24 h-auto rotate-180" />
                    </button>
                    <button className="next w-32 h-32 rounded-full lc:border border-grey-900 flex justify-center items-center pl-2">
                      <ChevronRightIcon className="w-16 ml:w-24 h-auto" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Swiper
          modules={[Navigation, FreeMode]}
          navigation={{
            prevEl: `[data-slider="${id}"] .prev`,
            nextEl: `[data-slider="${id}"] .next`,
            disabledClass: 'text-disabled !border-grey-400',
          }}
          className={clsx('max-w-container mx-auto', {
            'ml:md:[&_.swiper-wrapper]:justify-center': products.length < 5,
          })}
          slidesPerView={'auto'}
          freeMode={true}
          observer={true}
          spaceBetween={4}
          onInit={handleSwiperInit}
          breakpoints={{
            768: {
              spaceBetween: 8,
            },
          }}
        >
          {products.map((product) => (
            <SwiperSlide
              key={product.id}
              className="!w-[247px] md:!w-[calc((100%_-_24px)/4)] tablet:!w-[calc((100%_-_24px)/5)]"
            >
              <ProductCard key={product.id} product={product} {...props} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    )
  );
};

export default ProductRecommendations;
